import { Component, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageStore } from './shared/store/language/language.store';
import { SessionQuery, SessionStore } from './shared/session/state';
import { CountdownService } from './shared/services/countdown.service';
import { AdBlockerService } from './shared/services/ad-blocker.service';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { LoginLoggerService } from './shared/services/login-logger.service';
import { AppUpdateRecoveryService } from './shared/services/app-update-recovery.service';
import { VersionCheckService } from './shared/services/version-check.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AppComponent {
	private browserLanguage: string;
	public title: string = 'infoday.io';

	constructor(
		private translocoService: TranslocoService,
		private translocoLocaleService: TranslocoLocaleService,
		private languageStore: LanguageStore,
		private sessionQuery: SessionQuery,
		private countdownService: CountdownService,
		private authStore: SessionStore,
		private adBlockerService: AdBlockerService,
		private loginLoggerService: LoginLoggerService,
		private appUpdateRecoveryService: AppUpdateRecoveryService,
		private versionCheckService: VersionCheckService
	) {
		this.initializeApp();
	}

	initializeApp(): void {
		let savedLang = localStorage.getItem('infoday_language');
		let savedLocale = localStorage.getItem('infoday_locale');

		this.appUpdateRecoveryService.init();
		this.versionCheckService.initVersionCheck(60000);

		if (savedLang) {
			if (savedLang !== 'de' && savedLang !== 'en') {
				savedLang = 'en';
			}
			this.browserLanguage = savedLang;
			this.translocoService.setActiveLang(savedLang);
			this.languageStore.update({ lang: this.browserLanguage });
			localStorage.setItem('infoday_language', this.browserLanguage);

			if (savedLocale !== 'de-DE' && savedLocale !== 'en-US' && savedLocale !== 'fr-FR' && savedLocale !== 'es-ES') {
				savedLocale = 'en-US';
			}
			if (savedLocale) {
				this.translocoLocaleService.setLocale(savedLocale);
			}
			localStorage.setItem('infoday_locale', savedLocale);
		} else {
			// @ts-ignore
			if (
				'de-DE' === navigator.language ||
				// @ts-ignore
				'de-DE' === navigator.userLanguage
			) {
				this.browserLanguage = 'de';
				this.translocoService.setActiveLang('de');
				this.translocoLocaleService.setLocale('de-DE');
				this.languageStore.update({ lang: this.browserLanguage });
				localStorage.setItem('infoday_language', this.browserLanguage);
				localStorage.setItem('infoday_locale', 'de-DE');
				// @ts-ignore
			} else if (
				'fr-FR' === navigator.language ||
				// @ts-ignore
				'fr-FR' === navigator.userLanguage
			) {
				this.browserLanguage = 'fr';
				this.translocoService.setActiveLang('fr');
				this.translocoLocaleService.setLocale('fr-FR');
				this.languageStore.update({ lang: this.browserLanguage });
				localStorage.setItem('infoday_language', this.browserLanguage);
				localStorage.setItem('infoday_locale', 'fr-FR');
				// @ts-ignore
			} else if (
				'es-ES' === navigator.language ||
				// @ts-ignore
				'es-ES' === navigator.userLanguage
			) {
				this.browserLanguage = 'es';
				this.translocoService.setActiveLang('es');
				this.translocoLocaleService.setLocale('es-ES');
				this.languageStore.update({ lang: this.browserLanguage });
				localStorage.setItem('infoday_language', this.browserLanguage);
				localStorage.setItem('infoday_locale', 'es-ES');
			} else {
				this.translocoLocaleService.setLocale('en-US');
				this.browserLanguage = this.translocoService.getDefaultLang();
				this.languageStore.update({ lang: this.browserLanguage });
				localStorage.setItem('infoday_language', this.browserLanguage);
				localStorage.setItem('infoday_locale', 'en-US');
			}
		}

		this.adBlockerService.checkAdBlocker();

		if (this.sessionQuery.isLoggedIn()) {
			this.loginLoggerService.startLoginLogging(this.sessionQuery.getUserId());
		}

		this.checkIfSessionExpired();
	}

	checkIfSessionExpired(): void {
		const expires = this.sessionQuery.getExpire();

		if (!expires) {
			return;
		}

		if (this.countdownService.getTimeDifference(expires) <= 0) {
			this.authStore.logout();
		}
	}
}
